import type {
  Note,
  NoteListItem,
  NoteMapItem,
  NoteMapLinkedTask,
  NoteAttachment,
} from '@/types/notes';
import type {
  NoteResponse,
  NoteListItemResponse,
  NoteMapResponse,
  NoteAttachmentResponse,
} from '@/types/api/responses';
import { NoteVisibility } from '@/types/notes';
import { mapRelatedTaskSummaryFromResponse } from './taskMapper';

/**
 * Maps a note attachment API response to a frontend NoteAttachment.
 */
function mapNoteAttachmentFromApi(
  response: NoteAttachmentResponse
): NoteAttachment {
  return {
    id: response.id,
    fileName: response.fileName,
    contentType: response.contentType,
    fileSize: response.fileSize,
    createdAt: new Date(response.createdAt),
  };
}

/**
 * Maps a note API response to a frontend Note.
 */
export function mapNoteFromApi(response: NoteResponse): Note {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    content: response.content,
    latitude: response.latitude,
    longitude: response.longitude,
    location: response.location ?? null,
    color: response.color,
    visibility: response.visibility as NoteVisibility,
    createdBy: response.createdBy,
    creatorName: response.creatorName,
    creatorAvatarUrl: response.creatorAvatarUrl,
    attachmentCount: response.attachmentCount,
    linkedTaskCount: response.linkedTaskCount,
    attachments: response.attachments.map(mapNoteAttachmentFromApi),
    linkedTaskIds: response.linkedTaskIds,
    linkedTasks: (response.linkedTasks ?? []).map(
      mapRelatedTaskSummaryFromResponse
    ),
    createdAt: new Date(response.createdAt),
    updatedAt: response.updatedAt ? new Date(response.updatedAt) : null,
  };
}

/**
 * Maps a note list item API response to a frontend NoteListItem.
 * Used for paginated list views where full note content is not needed.
 */
export function mapNoteListItemFromApi(
  response: NoteListItemResponse
): NoteListItem {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    latitude: response.latitude,
    longitude: response.longitude,
    location: response.location ?? null,
    color: response.color,
    visibility: response.visibility as NoteVisibility,
    createdBy: response.createdBy,
    creatorName: response.creatorName,
    creatorAvatarUrl: response.creatorAvatarUrl,
    attachmentCount: response.attachmentCount,
    linkedTaskCount: response.linkedTaskCount,
    createdAt: new Date(response.createdAt),
    updatedAt: response.updatedAt ? new Date(response.updatedAt) : null,
  };
}

/**
 * Converts a full Note domain object to a lightweight NoteListItem.
 * Used when CRUD operations return full NoteResponse but the list stores lightweight items.
 */
export function noteToListItem(note: Note): NoteListItem {
  return {
    id: note.id,
    shortId: note.shortId,
    title: note.title,
    latitude: note.latitude,
    longitude: note.longitude,
    location: note.location,
    color: note.color,
    visibility: note.visibility,
    createdBy: note.createdBy,
    creatorName: note.creatorName,
    creatorAvatarUrl: note.creatorAvatarUrl,
    attachmentCount: note.attachmentCount,
    linkedTaskCount: note.linkedTaskCount,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  };
}

/**
 * Projects any linked-task-like source to the lightweight NoteMapLinkedTask
 * shape used by map hover-cards. Shared by the API and domain mappers.
 */
function toMapLinkedTask(linkedTask: {
  id: string;
  shortId: string;
  title: string;
}): NoteMapLinkedTask {
  return {
    id: linkedTask.id,
    shortId: linkedTask.shortId,
    title: linkedTask.title,
  };
}

/**
 * Maps a note map API response to a frontend NoteMapItem.
 */
export function mapNoteMapItemFromApi(response: NoteMapResponse): NoteMapItem {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    latitude: response.latitude,
    longitude: response.longitude,
    location: response.location ?? null,
    color: response.color,
    visibility: response.visibility as NoteVisibility,
    authorId: response.createdBy,
    creatorName: response.creatorName,
    creatorAvatarUrl: response.creatorAvatarUrl,
    linkedTasks: response.linkedTasks.map(toMapLinkedTask),
    createdAt: response.createdAt,
  };
}

/**
 * Converts a full Note domain object to a lightweight NoteMapItem.
 * Returns null when the note has no coordinates (it does not belong on the map).
 * Used by create/update flows to keep map markers in sync without a refetch
 * (mirrors the tasks slice pattern).
 */
export function noteToMapItem(note: Note): NoteMapItem | null {
  if (note.latitude === null || note.longitude === null) {
    return null;
  }
  return {
    id: note.id,
    shortId: note.shortId,
    title: note.title,
    latitude: note.latitude,
    longitude: note.longitude,
    location: note.location,
    color: note.color,
    visibility: note.visibility,
    authorId: note.createdBy,
    creatorName: note.creatorName,
    creatorAvatarUrl: note.creatorAvatarUrl,
    linkedTasks: note.linkedTasks.map(toMapLinkedTask),
    createdAt: note.createdAt.toISOString(),
    updatedAt: note.updatedAt ? note.updatedAt.toISOString() : null,
  };
}
